import {useState, useEffect, useCallback} from 'react'

// Simplified states focusing on listening/sending loop
type ContinuousCycleStage =
  | 'idle' // Waiting for trigger (AI message or initial start)
  | 'listening' // Recording active, waiting for speech/end

/**
 * Custom hook to manage continuous voice interaction
 *
 * This hook handles the continuous voice flow:
 * 1. Activated when AI message arrives or mode is toggled.
 * 2. Enters listening state.
 * 3. Detects speech end.
 * 4. Waits for final input confirmation.
 * 5. Resets to idle.
 * 6. External component sends message.
 */
export function useContinuousVoice() {
  // State for the continuous mode toggle
  const [isContinuousMode, setIsContinuousMode] = useState(false)

  // State to track the current stage of the continuous cycle
  const [continuousCycleStage, setContinuousCycleStage] = useState<
    ContinuousCycleStage
  >('idle')

  // Track if first user interaction has occurred to avoid audio context errors
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  // Track user interactions for initial audio unlock
  const handleUserInteraction = useCallback(() => {
    // Mark that the user has interacted with the page
    if (!hasUserInteracted) {
      setHasUserInteracted(true)
    }
  }, [hasUserInteracted])

  // Set up event listeners for user interaction
  useEffect(() => {
    // Add user interaction listeners - always listen for first interaction
    document.addEventListener('click', handleUserInteraction)
    document.addEventListener('touchstart', handleUserInteraction)
    document.addEventListener('keydown', handleUserInteraction)

    return () => {
      // Clean up listeners
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('touchstart', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
    }
  }, [isContinuousMode, handleUserInteraction])

  // Explicitly set continuous mode state
  const setContinuousMode = useCallback(
    (enabled: boolean) => {
      if (!hasUserInteracted && enabled) {
        /*console.warn(
          'Cannot enable continuous mode until user interaction has occurred - forcing interaction state'
        )*/
        // Force user interaction state to allow enabling continuous mode
        setHasUserInteracted(true)
      }

      setIsContinuousMode(enabled)
      // Always reset to idle when mode changes
      setContinuousCycleStage('idle')

      // If turning off continuous mode, stop any ongoing audio (external check needed)
      // External component should handle stopping TTS/Recording

      // Emit an event that can be listened to for handling message history
      const event = new CustomEvent('continuousModeChanged', {
        detail: {enabled},
      })
      document.dispatchEvent(event)

      return enabled
    },
    [hasUserInteracted]
  )

  // Toggle continuous mode on/off
  const toggleContinuousMode = useCallback(() => {
    // Don't allow enabling continuous mode until user has interacted
    if (!hasUserInteracted && !isContinuousMode) {
      /*console.warn(
        'Cannot enable continuous mode until user interaction has occurred'
      )*/
      return false
    }

    const newModeState = !isContinuousMode
    setIsContinuousMode(newModeState)
    // Always reset to idle when mode changes
    setContinuousCycleStage('idle')

    // External component handles stopping audio/recording when toggling off

    // Emit an event that can be listened to for handling message history
    const event = new CustomEvent('continuousModeChanged', {
      detail: {enabled: newModeState},
    })
    document.dispatchEvent(event)

    // console.log(`Continuous mode ${newModeState ? 'enabled' : 'disabled'}`)

    // Return whether continuous mode is now enabled
    return newModeState
  }, [isContinuousMode, hasUserInteracted])

  /**
   * Sets the cycle stage to listening. Should be called when recording starts.
   */
  const startListening = useCallback(() => {
    if (!hasUserInteracted || !isContinuousMode) return false
    // Can transition from idle
    setContinuousCycleStage('listening')
    // console.log('Continuous Voice: State -> listening')
    return true
  }, [isContinuousMode, hasUserInteracted])

  /**
   * Resets the cycle stage to idle. Called after message is sent or mode is toggled off.
   */
  const resetToIdle = useCallback(() => {
    // Allow resetting from any state
    setContinuousCycleStage('idle')
    // console.log('Continuous Voice: State -> idle')
    return true
  }, []) // No dependencies needed for reset

  return {
    isContinuousMode,
    continuousCycleStage,
    toggleContinuousMode,
    setContinuousMode,
    // Renamed/New handlers
    startListening,
    resetToIdle,
    // Keep user interaction state
    hasUserInteracted,
    setHasUserInteracted,
  }
}
